import { formatCsvImportToast, type CsvImportSummary } from './csvRejectionDiagnostics';
import { getCsvImportErrorMessage } from './csvImportPersistenceSync';

export type CsvImportToastInput = {
  success: boolean;
  count?: number;
  persistedTradeCount?: number;
  accountName?: string;
  executionCount?: number;
  duplicateCount?: number;
  skippedInvalidCount?: number;
  rejectedCount?: number;
  rowsRead?: number;
  summary?: CsvImportSummary | null;
  message?: string;
};

export type CsvImportToastDetail = { title: string; message: string; variant: 'info' | 'success' | 'error' };

export function buildCsvImportToastDetail(res: CsvImportToastInput): CsvImportToastDetail {
  const accountName = String(res.accountName || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const accountSuffix = accountName ? ` in ${accountName}` : ' in this account';
  if (!res.success) {
    const failure = getCsvImportErrorMessage(res.message, 'The CSV file could not be processed.');
    return { title: 'Import Failed', message: accountName ? `${accountName}: ${failure}` : failure, variant: 'error' };
  }

  const persistedCount = Number(res.persistedTradeCount);
  const imported = Number.isFinite(persistedCount) && persistedCount >= 0
    ? persistedCount
    : Number(res.summary?.newTradesImported ?? res.count ?? 0);
  const duplicateTrades = Number(res.summary?.duplicateTradesSkipped ?? 0);
  const duplicateRows = Number(res.summary?.sourceRowsByOutcome?.duplicate ?? res.duplicateCount ?? 0);
  const duplicateExecutions = Number(res.summary?.duplicateExecutionsSkipped ?? 0);
  const skipped = res.summary
    ? Number(res.summary.sourceRowsByOutcome.invalid || 0)
      + Number(res.summary.sourceRowsByOutcome.rejected || 0)
      + Number(res.summary.sourceRowsByOutcome.tenantRestricted || 0)
      + Number(res.summary.sourceRowsByOutcome.unclassified || 0)
    : Number(res.skippedInvalidCount || 0) + Number(res.rejectedCount || 0);

  if (imported === 0 && (duplicateTrades > 0 || duplicateRows > 0 || duplicateExecutions > 0) && skipped === 0) {
    const message = duplicateTrades > 0
      ? `All ${duplicateTrades} ${duplicateTrades === 1 ? 'trade already exists' : 'trades already exist'}${accountSuffix}.`
      : `All ${duplicateExecutions || duplicateRows} ${duplicateExecutions === 1 ? 'execution already exists' : 'executions already exist'}${accountSuffix}.`;
    return { title: 'No New Trades', message, variant: 'info' };
  }

  if (res.summary && imported > 0 && (duplicateTrades > 0 || duplicateExecutions > 0) && skipped === 0) {
    const duplicates = duplicateTrades || duplicateExecutions;
    const duplicateLabel = duplicateTrades
      ? `${duplicates} ${duplicates === 1 ? 'duplicate' : 'duplicates'} skipped.`
      : `${duplicates} duplicate ${duplicates === 1 ? 'execution' : 'executions'} skipped.`;
    return {
      title: 'Import Complete',
      message: `${imported} ${imported === 1 ? 'trade' : 'trades'} imported${accountName ? ` into ${accountName}` : ''}. ${duplicateLabel}`,
      variant: 'success',
    };
  }

  let message = res.summary
    ? formatCsvImportToast(res.summary)
    : imported > 0
      ? `Import complete — ${imported} ${imported === 1 ? 'trade' : 'trades'} imported successfully.`
      : `No new trades imported.${skipped > 0 ? ` ${skipped} rows could not be imported.` : ''}`;
  message = message.replace(/^Import complete\s*[—-]\s*/i, '');
  if (accountName) {
    message = imported > 0 && !res.summary
      ? `${imported} ${imported === 1 ? 'trade' : 'trades'} imported successfully into ${accountName}.`
      : `${message} (${accountName})`;
  }
  return {
    title: imported > 0 ? 'Import Complete' : 'No New Trades',
    message,
    variant: imported > 0 ? 'success' : 'info',
  };
}
